import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { sanitizeEvaluationPromptHtml } from '../utils/evaluationPrompt';
import ReactMarkdown from 'react-markdown';
import { apiService } from '../services/api';
import { authUtils } from '../services/auth';
import Header from '../components/common/Header';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Button from '../components/common/Button';
import { Competition } from '../types/interfaces';
import RubricScoreForm, { CompetitionRubric } from '../components/competition/RubricScoreForm';
import SingleFileViewer from '../components/viewers/SingleFileViewer';
import MultiPackageView from '../components/viewers/MultiPackageView';
import { ProjectSubmissionView } from '../components/viewers/ProjectSubmission';
import { MultiManifest, trackLabelFor, usesProjectLayout } from '../components/viewers/submissionMedia';
import RelativeTime from '../components/common/RelativeTime';
import { shouldOpenInstructions } from './evaluations/instructionsSeen';
import './Evaluations.css';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

interface SubmissionMedia {
  user_id: number;
  url: string;
  team_id?: number;
  team_name?: string;
  reservation_token?: string;
  reservation_ttl_seconds?: number;
}

interface ManualScoringSubmission {
  submission_id: number;
  submission_version: number;
  user_id?: number;
  username?: string;
  team_id?: number;
  team_name?: string;
  url: string;
  manifest?: MultiManifest | null;
  eval_count?: number;
  already_scored?: boolean;
  metadata_title?: string;
  metadata_track?: string;
  metadata_team?: string;
  criterion_scores?: { criterion_id: number; score: number }[];
  reviewed_submission_version?: number;
  evaluation_feedback?: string | null;
}

interface ManualSubmissionListItem {
  submission_id: number;
  submission_version: number;
  user_id?: number;
  username?: string;
  team_id?: number;
  team_name?: string;
  submitted_at: string;
  eval_count: number;
  already_scored: boolean;
  metadata_title?: string;
  metadata_track?: string;
  metadata_team?: string;
}

interface ManualScoreSubmitResponse {
  status: string;
  manual_score_id: number;
  submission_id: number;
  submission_version: number;
  score_submitted: number;
  average_score: number;
  total_scores: number;
}

interface VotesInfo {
  current_votes: number;
  max_votes: number;
}

const VIDEO_VOTE_DELAY_BY_COMPETITION: Record<number, number> = {
  5: 5 * 60, // 5 minutes for competition 5
  11: 5 * 60, // 5 minutes for competition 11
};

const DEFAULT_PAIRWISE_EVALUATION_PROMPT_HTML =
  'Compare the two submissions below and vote for the best one, or select <strong>Draw</strong> if they are equally good.';
const DEFAULT_MULTI_EVALUATION_PROMPT_HTML =
  'Review the submission below and assign a manual score from <strong>1 to 100</strong>.';
const DEFAULT_AGRICULTURE_RUBRIC_HTML = `
  <ul>
    <li><strong>Agricultural relevance:</strong> How clearly does the project address a meaningful problem in its selected track?</li>
    <li><strong>Innovation:</strong> How useful and original is the proposed approach?</li>
    <li><strong>Technical feasibility:</strong> How well do the methods, prototype, and evidence support practical implementation?</li>
    <li><strong>Expected impact:</strong> What benefits could the project deliver for productivity, sustainability, animal welfare, or agricultural systems?</li>
    <li><strong>Presentation clarity:</strong> How clearly do the description, slides, and video communicate the problem, solution, and results?</li>
  </ul>
  <p>Use these criteria to inform one overall score from <strong>1 to 100</strong>.</p>
`;

function getManualSubmissionDisplayLabel(item: {
  metadata_title?: string;
  team_name?: string;
  username?: string;
  submission_id: number;
}): string {
  const title = (item.metadata_title || '').trim();
  if (title) return title;
  if (item.team_name) return item.team_name;
  if (item.username) return item.username;
  return `Submission #${item.submission_id}`;
}

function extractApiError(err: unknown, fallback: string): string {
  const maybeResponse = (err as { response?: { data?: { detail?: string; message?: string } } }).response;
  const detail = maybeResponse?.data?.detail || maybeResponse?.data?.message;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  const message = (err as Error)?.message;
  if (typeof message === 'string' && message.trim()) {
    return message;
  }
  return fallback;
}

function isEmptyManualSelection(err: unknown): boolean {
  const response = (err as { response?: { status?: number; data?: { detail?: string } } }).response;
  return response?.status === 404 && ['No available submissions for manual scoring', 'No unevaluated projects available', 'No remaining evaluation allowance'].includes(response.data?.detail || '');
}

const ALLOWANCE_USED_UP_MESSAGE = "You've used all your evaluations. Thank you for judging!";

const Evaluations: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [vote, setVote] = useState<string | null>(null);
  const [mediaPair, setMediaPair] = useState<SubmissionMedia[]>([]);
  const [manualSubmission, setManualSubmission] = useState<ManualScoringSubmission | null>(null);
  const [manualSubmissionList, setManualSubmissionList] = useState<ManualSubmissionListItem[]>([]);
  const [manualSubmissionListLoading, setManualSubmissionListLoading] = useState(false);
  const [manualSubmissionListError, setManualSubmissionListError] = useState('');
  const [manualSelectionLoading, setManualSelectionLoading] = useState(false);
  const [projectTrackFilter, setProjectTrackFilter] = useState<string>('all');
  const [manualScoreInput, setManualScoreInput] = useState<string>('');
  const [manualScoreResult, setManualScoreResult] = useState<ManualScoreSubmitResponse | null>(null);

  const [rubric, setRubric] = useState<CompetitionRubric | null>(null);
  const [scoringReady, setScoringReady] = useState(false);
  const [scoringError, setScoringError] = useState('');
  const [competition, setCompetition] = useState<Competition | null>(null);
  useDocumentTitle(competition?.title ? `Evaluate · ${competition.title}` : undefined);
  const [competitionLoaded, setCompetitionLoaded] = useState(false);
  const [votesInfo, setVotesInfo] = useState<VotesInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMediaUrl, setSelectedMediaUrl] = useState<string | null>(null);
  const [feedback, setFeedback] = useState('');
  const [submittingVote, setSubmittingVote] = useState(false);
  const [voteUnlockAtMs, setVoteUnlockAtMs] = useState<number | null>(null);
  const [timerNowMs, setTimerNowMs] = useState<number>(Date.now());

  const [evaluationMarkdown, setEvaluationMarkdown] = useState<string>('');
  const [evaluationMarkdownLoading, setEvaluationMarkdownLoading] = useState(false);
  const [evaluationMarkdownError, setEvaluationMarkdownError] = useState('');
  const [evaluationMarkdownExpanded, setEvaluationMarkdownExpanded] = useState(false);
  // Open the instructions on a judge's first visit, once the panel actually exists for this competition.
  const instructionsCheckedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!id || !competition?.evaluation_path || instructionsCheckedFor.current === id) return;
    instructionsCheckedFor.current = id;
    if (shouldOpenInstructions(id)) setEvaluationMarkdownExpanded(true);
  }, [id, competition?.evaluation_path]);

  const competitionType = competition?.type?.toLowerCase();
  const isVideoCompetition = competitionType === 'video';
  const isPdfCompetition = competitionType === 'pdf';
  const isHtmlCompetition = competitionType === 'html';
  const isGithubCompetition = competitionType === 'github';
  const isMultiCompetition = competitionType === 'multi';
  const isRubricCompetition = Boolean(rubric);
  const isCompetition18 = Number(id) === 18;
  const feedbackRequired = Boolean(competition?.feedback_required);
  const minFeedbackChars = 10;
  const hasEnoughFeedback = feedback.trim().length >= minFeedbackChars;

  const rawEvaluationPromptHtml = competition?.evaluation?.trim()
    || (isMultiCompetition
      ? isCompetition18 ? DEFAULT_AGRICULTURE_RUBRIC_HTML : DEFAULT_MULTI_EVALUATION_PROMPT_HTML
      : DEFAULT_PAIRWISE_EVALUATION_PROMPT_HTML);
  const evaluationPromptHtml = useMemo(
    () => sanitizeEvaluationPromptHtml(rawEvaluationPromptHtml),
    [rawEvaluationPromptHtml],
  );

  const competitionIdNumber = Number(id);
  const voteDelaySeconds = (!isMultiCompetition && isVideoCompetition && Number.isFinite(competitionIdNumber))
    ? (VIDEO_VOTE_DELAY_BY_COMPETITION[competitionIdNumber] || 0)
    : 0;
  const pairKey = mediaPair.length === 2
    ? [mediaPair[0].user_id, mediaPair[1].user_id].sort((a, b) => a - b).join('-')
    : null;
  const voteDelayStorageKey = (voteDelaySeconds > 0 && pairKey)
    ? `ikompete:vote-unlock:${competitionIdNumber}:${pairKey}`
    : null;
  const voteDelaySecondsLeft = voteUnlockAtMs
    ? Math.max(Math.ceil((voteUnlockAtMs - timerNowMs) / 1000), 0)
    : 0;
  const voteDelayActive = voteDelaySeconds > 0 && voteDelaySecondsLeft > 0 && vote === null;

  const usesProjectSubmissionLayout = isMultiCompetition && usesProjectLayout(competitionIdNumber);
  const getTrackLabel = useCallback(
    (rawTrack?: string | null): string => trackLabelFor(competitionIdNumber, rawTrack),
    [competitionIdNumber],
  );

  const projectTrackOptions = useMemo(() => {
    if (!usesProjectSubmissionLayout) return [];
    const uniqueTracks = new Set<string>();
    manualSubmissionList.forEach((item) => {
      const track = (item.metadata_track || '').trim();
      if (track) uniqueTracks.add(track);
    });
    return Array.from(uniqueTracks).sort((a, b) => getTrackLabel(a).localeCompare(getTrackLabel(b)));
  }, [usesProjectSubmissionLayout, getTrackLabel, manualSubmissionList]);
  const visibleManualSubmissionList = useMemo(() => {
    if (!usesProjectSubmissionLayout || projectTrackFilter === 'all') {
      return manualSubmissionList;
    }
    return manualSubmissionList.filter(
      (item) => ((item.metadata_track || '').trim() === projectTrackFilter),
    );
  }, [usesProjectSubmissionLayout, projectTrackFilter, manualSubmissionList]);

  const formatDelay = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
  };

  const fetchVotesInfo = async (competitionId: string) => {
    const token = authUtils.getStoredToken();
    if (isRubricCompetition) {
      const response = await apiService.get(`/evaluations/rubric/${competitionId}/status`);
      setVotesInfo({ current_votes: response.data.completed_evaluations, max_votes: response.data.max_evaluations });
      return;
    }
    const response = await apiService.post(`/evaluations/check_votes/${competitionId}`, {}, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    setVotesInfo(response.data);
  };

  const fetchManualSubmissionList = async (competitionId: string) => {
    if (!isMultiCompetition) return;
    try {
      setManualSubmissionListLoading(true);
      setManualSubmissionListError('');
      const token = authUtils.getStoredToken();
      const response = isRubricCompetition
        ? await apiService.get(`/evaluations/rubric/${competitionId}/submissions`)
        : await apiService.post(`/evaluations/list_submissions/${competitionId}`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setManualSubmissionList(response.data as ManualSubmissionListItem[]);
    } catch (err) {
      setManualSubmissionListError(extractApiError(err, 'Failed to load available submissions'));
      console.error('Error loading manual submission list:', err);
    } finally {
      setManualSubmissionListLoading(false);
    }
  };

  const handleSelectManualSubmission = async (submissionId: number) => {
    if (!id || !isMultiCompetition || submittingVote) return;

    try {
      setManualSelectionLoading(true);
      setError('');
      const token = authUtils.getStoredToken();
      const response = isRubricCompetition
        ? await apiService.get(`/evaluations/rubric/${id}/submissions/${submissionId}`)
        : await apiService.post(
        `/evaluations/get_submission_by_id/${id}/${submissionId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setManualSubmission(response.data as ManualScoringSubmission);
      setManualScoreInput('');
      setManualScoreResult(null);
      setFeedback('');
    } catch (err) {
      setError(extractApiError(err, 'Failed to load selected submission'));
      console.error('Error loading selected manual submission:', err);
    } finally {
      setManualSelectionLoading(false);
    }
  };

  // Fetch competition data
  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const fetchCompetition = async () => {
      try {
        setCompetitionLoaded(false);
        setScoringReady(false);
        setScoringError('');
        setRubric(null);
        const token = authUtils.getStoredToken();
        const response = await apiService.post(`/competitions/${id}`, {}, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!cancelled) setCompetition(response.data);
        if (response.data.type?.toLowerCase() === 'multi') {
          try {
            const configResponse = await apiService.get(`/evaluations/rubric/${id}`);
            if (!cancelled) setRubric(configResponse.data.rubric || null);
          } catch (err) {
            if (!cancelled) {
              setScoringError(extractApiError(err, 'Could not load the scoring configuration.'));
              setLoading(false);
            }
            return;
          }
        }
        if (!cancelled) setScoringReady(true);
      } catch (err) {
        console.error('Error loading competition:', err);
        if (!cancelled) {
          setCompetition(null);
        }
      } finally {
        if (!cancelled) {
          setCompetitionLoaded(true);
        }
      }
    };

    fetchCompetition();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Fetch evaluation markdown on-demand (only when expanded)
  useEffect(() => {
    const path = competition?.evaluation_path;
    if (!evaluationMarkdownExpanded) return;
    if (!path) return;
    if (evaluationMarkdown || evaluationMarkdownLoading) return;

    const run = async () => {
      try {
        setEvaluationMarkdownLoading(true);
        setEvaluationMarkdownError('');
        const resp = await fetch(path);
        if (!resp.ok) {
          throw new Error(`Failed to fetch evaluation instructions (${resp.status})`);
        }
        const text = await resp.text();
        setEvaluationMarkdown(text);
      } catch (err) {
        console.error('Error loading evaluation markdown:', err);
        setEvaluationMarkdownError('Failed to load evaluation instructions');
      } finally {
        setEvaluationMarkdownLoading(false);
      }
    };

    run();
  }, [competition?.evaluation_path, evaluationMarkdownExpanded, evaluationMarkdown, evaluationMarkdownLoading]);

  // Fetch votes info
  useEffect(() => {
    if (!id || !scoringReady) return;
    const run = async () => {
      try {
        await fetchVotesInfo(id);
      } catch (err) {
        console.error('Error loading votes info:', err);
      }
    };
    run();
  }, [id, scoringReady, isRubricCompetition]);

  useEffect(() => {
    if (!id || !competitionLoaded || !scoringReady || !isMultiCompetition) return;
    fetchManualSubmissionList(id).catch((err) => {
      console.error('Error refreshing manual submission list:', err);
    });
  }, [id, competitionLoaded, scoringReady, isMultiCompetition, isRubricCompetition]);

  // Fetch evaluation unit: pairwise pair or multi manual submission.
  useEffect(() => {
    if (!id || !competitionLoaded || !scoringReady) return;
    let cancelled = false;

    const fetchEvaluationUnit = async () => {
      setLoading(true);
      setError('');
      try {
        const token = authUtils.getStoredToken();

        if (isMultiCompetition) {
          const response = isRubricCompetition
            ? await apiService.get(`/evaluations/rubric/${id}/submission`)
            : await apiService.post(`/evaluations/get_submission/${id}`, {}, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          if (!cancelled) {
            setManualSubmission(response.data as ManualScoringSubmission);
            setMediaPair([]);
            setVote(null);
            setManualScoreInput('');
            setManualScoreResult(null);
            setFeedback('');
          }
        } else {
          const response = await apiService.post(`/evaluations/get_pair/${id}`, {}, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          if (!cancelled) {
            setMediaPair(response.data);
            setManualSubmission(null);
            setVote(null);
            setManualScoreInput('');
            setManualScoreResult(null);
            setFeedback('');
          }
        }
      } catch (err) {
        if (!cancelled) {
          if (isMultiCompetition && isEmptyManualSelection(err)) {
            setManualSubmission(null);
            setMediaPair([]);
            setError('');
          } else {
            setError(extractApiError(err, 'Failed to load submissions'));
          }
        }
        console.error('Error fetching evaluations payload:', err);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchEvaluationUnit();

    return () => {
      cancelled = true;
    };
  }, [id, competitionLoaded, scoringReady, isMultiCompetition, isRubricCompetition]);

  // Competition-specific vote unlock timer per pair (persists across refreshes).
  useEffect(() => {
    if (!voteDelayStorageKey) {
      setVoteUnlockAtMs(null);
      return;
    }

    const now = Date.now();
    let unlockAt = now + (voteDelaySeconds * 1000);
    try {
      const storedUnlockAt = Number(window.localStorage.getItem(voteDelayStorageKey));
      if (Number.isFinite(storedUnlockAt) && storedUnlockAt > now) {
        unlockAt = storedUnlockAt;
      } else {
        window.localStorage.setItem(voteDelayStorageKey, String(unlockAt));
      }
    } catch (err) {
      // Ignore localStorage issues; timer will still work for current page state.
      console.warn('Unable to access vote timer storage', err);
    }

    setTimerNowMs(now);
    setVoteUnlockAtMs(unlockAt);
  }, [voteDelayStorageKey, voteDelaySeconds]);

  useEffect(() => {
    if (!voteUnlockAtMs || vote !== null) return;

    const interval = window.setInterval(() => {
      const now = Date.now();
      setTimerNowMs(now);
      if (now >= voteUnlockAtMs) {
        window.clearInterval(interval);
      }
    }, 1000);

    return () => window.clearInterval(interval);
  }, [voteUnlockAtMs, vote]);

  useEffect(() => {
    if (
      !selectedMediaUrl
      || isVideoCompetition
      || isPdfCompetition
      || isHtmlCompetition
      || isGithubCompetition
      || isMultiCompetition
    ) {
      return;
    }
    window.open(selectedMediaUrl, '_blank');
    setSelectedMediaUrl(null);
  }, [selectedMediaUrl, isVideoCompetition, isPdfCompetition, isHtmlCompetition, isGithubCompetition, isMultiCompetition]);

  useEffect(() => {
    if (isMultiCompetition) return;
    setManualSubmissionList([]);
    setManualSubmissionListLoading(false);
    setManualSelectionLoading(false);
  }, [isMultiCompetition]);

  useEffect(() => {
    if (!usesProjectSubmissionLayout) {
      setProjectTrackFilter('all');
      return;
    }
    if (projectTrackFilter === 'all') return;
    const stillExists = projectTrackOptions.some((t) => t === projectTrackFilter);
    if (!stillExists) {
      setProjectTrackFilter('all');
    }
  }, [usesProjectSubmissionLayout, projectTrackFilter, projectTrackOptions]);

  const handleVote = async (choice: string) => {
    if (mediaPair.length !== 2 || !id) return;
    if (submittingVote) return;
    if (voteDelayActive) {
      setError(`Voting unlocks in ${formatDelay(voteDelaySecondsLeft)}.`);
      return;
    }
    if (feedbackRequired && !hasEnoughFeedback) {
      setError(`Feedback is required (min ${minFeedbackChars} characters).`);
      return;
    }

    const winnerMap: Record<string, string> = {
      A: 'user_a',
      B: 'user_b',
      Draw: 'draw',
    };

    const payload = {
      competition_id: parseInt(id, 10),
      user_a_id: mediaPair[0].user_id,
      user_b_id: mediaPair[1].user_id,
      winner: winnerMap[choice],
      feedback: feedback.trim() || null,
      reservation_token: mediaPair[0]?.reservation_token || mediaPair[1]?.reservation_token || null,
    };

    try {
      setSubmittingVote(true);
      setError('');
      const token = authUtils.getStoredToken();
      await apiService.post('/evaluations/add_match', payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setVote(choice);
      await fetchVotesInfo(id);
    } catch (err) {
      setError(extractApiError(err, 'Failed to submit vote'));
      console.error('Failed to submit vote:', err);
    } finally {
      setSubmittingVote(false);
    }
  };

  const handleSubmitManualScore = async () => {
    if (!manualSubmission || !id) return;
    if (submittingVote) return;

    const scoreValue = Number(manualScoreInput);
    if (!Number.isFinite(scoreValue)) {
      setError('Enter a valid score between 1 and 100.');
      return;
    }
    if (scoreValue < 1 || scoreValue > 100) {
      setError('Score must be between 1 and 100.');
      return;
    }
    if (feedbackRequired && !hasEnoughFeedback) {
      setError(`Feedback is required (min ${minFeedbackChars} characters).`);
      return;
    }

    try {
      setSubmittingVote(true);
      setError('');
      const token = authUtils.getStoredToken();
      const response = await apiService.post('/evaluations/add_manual_score', {
        competition_id: parseInt(id, 10),
        submission_id: manualSubmission.submission_id,
        submission_version: manualSubmission.submission_version,
        score: scoreValue,
        feedback: feedback.trim() || null,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setManualScoreResult(response.data as ManualScoreSubmitResponse);
      await fetchVotesInfo(id);
      await fetchManualSubmissionList(id);
    } catch (err) {
      setError(extractApiError(err, 'Failed to submit manual score'));
      console.error('Failed to submit manual score:', err);
    } finally {
      setSubmittingVote(false);
    }
  };

  const handleRefresh = () => {
    setVote(null);
    setMediaPair([]);
    setManualSubmission(null);
    setManualScoreInput('');
    setManualScoreResult(null);
    setLoading(true);
    setError('');
    setSelectedMediaUrl(null);
    setFeedback('');

    if (!id || !competitionLoaded) return;

    const token = authUtils.getStoredToken();
    const request = isMultiCompetition
      ? isRubricCompetition
        ? apiService.get(`/evaluations/rubric/${id}/submission`)
        : apiService.post(`/evaluations/get_submission/${id}`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      : apiService.post(`/evaluations/get_pair/${id}`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

    request.then((response) => {
      if (isMultiCompetition) {
        setManualSubmission(response.data as ManualScoringSubmission);
      } else {
        setMediaPair(response.data);
      }
      setLoading(false);
    }).catch((err) => {
      if (isMultiCompetition && isEmptyManualSelection(err)) {
        setManualSubmission(null);
        setError('');
      } else {
        setError(extractApiError(err, 'Failed to load submissions'));
      }
      setLoading(false);
    });

    fetchVotesInfo(id).catch((err) => {
      console.error('Error loading votes info:', err);
    });
    if (isMultiCompetition) {
      fetchManualSubmissionList(id).catch((err) => {
        console.error('Error loading manual submission list:', err);
      });
    }
  };

  if (scoringError) return <><Header /><div className="evaluations-page">
    <p role="alert" className="evaluations-error">{scoringError}</p>
    <Button onClick={() => window.location.reload()}>Retry</Button>
  </div></>;

  if (loading) {
    return (
      <>
        <Header />
        <div className="evaluations-page">
          <LoadingSpinner text="Loading evaluation..." />
        </div>
      </>
    );
  }

  const votesMax = Math.max(votesInfo?.max_votes ?? 0, 0);
  const votesDone = Math.min(Math.max(votesInfo?.current_votes ?? 0, 0), votesMax);
  const votesRemaining = Math.max(votesMax - (votesInfo?.current_votes ?? 0), 0);
  const allowanceUsedUp = Boolean(votesInfo) && votesMax > 0 && votesRemaining === 0;

  if (!isMultiCompetition && mediaPair.length !== 2) {
    return (
      <>
        <Header />
        <div className="evaluations-page">
          <button className="btn-back" onClick={() => window.history.back()}>
            &larr; Back
          </button>
          <div className="evaluations-empty" data-tour="eval-empty">
            <p>{allowanceUsedUp ? ALLOWANCE_USED_UP_MESSAGE : 'No more pairs to evaluate.'}</p>
          </div>
        </div>
      </>
    );
  }

  const manualSubmissionTitle = (() => {
    const explicitTitle = (manualSubmission?.metadata_title || '').trim();
    if (explicitTitle) return explicitTitle;
    if (manualSubmission?.team_name) return `Team • ${manualSubmission.team_name}`;
    if (manualSubmission?.username) return `Participant • ${manualSubmission.username}`;
    return 'Submission';
  })();

  return (
    <>
      <Header />
      <div className="evaluations-page">
        <button className="btn-back" onClick={() => window.history.back()}>
          &larr; Back
        </button>

        {competition && (
          <div className="evaluations-hero">
            <div className="evaluations-hero__title" data-tour="eval-hero">
              <h1>{competition.title}</h1>
              <div className="evaluations-hero__actions">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/competition/${competition.id}`)}
                >
                  See competition
                </Button>
              </div>
            </div>

            {votesInfo && (
              <div
                className={`evaluations-votes-card${allowanceUsedUp ? ' evaluations-votes-card--done' : ''}`}
                data-tour="eval-allowance"
                aria-label="Votes left"
              >
                <div className="evaluations-votes-card__label">
                  {allowanceUsedUp ? 'All done — thank you!' : 'Evaluations left'}
                </div>
                <div className="evaluations-votes-card__value">
                  <strong>{votesRemaining}</strong>
                  <span className="evaluations-votes-card__sub">/ {votesInfo.max_votes}</span>
                </div>
                {votesMax > 0 && (
                  <>
                    <div
                      className="evaluations-votes-card__progress"
                      role="progressbar"
                      aria-label="Evaluations completed"
                      aria-valuenow={votesDone}
                      aria-valuemin={0}
                      aria-valuemax={votesMax}
                    >
                      <div
                        className="evaluations-votes-card__progress-fill"
                        style={{ width: `${(votesDone / votesMax) * 100}%` }}
                      />
                    </div>
                    <div className="evaluations-votes-card__done">{votesDone} of {votesMax} done</div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {competition?.evaluation_path ? (
          <div className="evaluations-eval-instructions">
            <button
              type="button"
              className="evaluations-eval-instructions__toggle"
              data-tour="eval-instructions-toggle"
              onClick={() => setEvaluationMarkdownExpanded((v) => !v)}
              aria-expanded={evaluationMarkdownExpanded}
            >
              {isCompetition18
                ? evaluationMarkdownExpanded ? 'Hide rubric' : 'Show rubric'
                : evaluationMarkdownExpanded ? 'Hide instructions' : 'Show instructions'}
            </button>

            {evaluationMarkdownExpanded && (
              <div className="evaluations-eval-instructions__content">
                {evaluationMarkdownLoading ? (
                  <LoadingSpinner size="sm" text="Loading evaluation instructions..." />
                ) : evaluationMarkdownError ? (
                  <div className="evaluations-error evaluations-error--flush">
                    {evaluationMarkdownError}{' '}
                    <a href={competition.evaluation_path} target="_blank" rel="noopener noreferrer">
                      Open in new tab
                    </a>
                  </div>
                ) : (
                  <ReactMarkdown>{evaluationMarkdown}</ReactMarkdown>
                )}
              </div>
            )}
          </div>
        ) : null}

        <div className="evaluations-instructions" data-tour="eval-guidance">
          {isCompetition18 && isMultiCompetition && (
            <p>{isRubricCompetition ? "Review one project and score each rubric criterion from 1 to 5." : "Review one project using the rubric and submit one overall score from 1 to 100."}</p>
          )}
          {!isRubricCompetition && (!isCompetition18 || !isMultiCompetition || !competition?.evaluation_path) && (
            <>
              {isCompetition18 && isMultiCompetition && <h3>Evaluation rubric</h3>}
              <div
                className="evaluations-instructions__html"
                dangerouslySetInnerHTML={{ __html: evaluationPromptHtml }}
              />
            </>
          )}
        </div>

        {error && (
          <div className="evaluations-error">
            {error}
          </div>
        )}

        {isMultiCompetition ? (
          <>
            <div className="evaluations-manual-layout">
              <div className="evaluations-manual-submission-card" data-tour={manualSubmission ? 'eval-project' : undefined}>
                {manualSubmission ? (
                  <>
                    <div className="evaluations-manual-submission-title">{manualSubmissionTitle}</div>
                    {usesProjectSubmissionLayout ? (
                      <ProjectSubmissionView
                        submission={manualSubmission}
                        competitionId={competitionIdNumber}
                        meta={(
                          <>
                            <span>Project submission</span>
                            {typeof manualSubmission.eval_count === 'number' && (
                              <span>Evaluations so far: {manualSubmission.eval_count}</span>
                            )}
                            {manualSubmission.already_scored && (
                              <span className="evaluations-multi-pill">Already scored by you</span>
                            )}
                            <a href={manualSubmission.url} target="_blank" rel="noopener noreferrer">
                              Open raw manifest
                            </a>
                          </>
                        )}
                      />
                    ) : (
                      <MultiPackageView submission={manualSubmission} />
                    )}
                  </>
                ) : (
                  <div className="evaluations-empty" data-tour="eval-project-empty">
                    <p>{manualSubmissionListLoading
                      ? 'Loading available projects...'
                      : allowanceUsedUp
                        ? ALLOWANCE_USED_UP_MESSAGE
                        : manualSubmissionList.length > 0
                        ? 'Select a project from Available submissions to review and score it.'
                        : 'No eligible projects yet.'}</p>
                    {!manualSubmissionListLoading && !allowanceUsedUp && manualSubmissionList.length === 0 && (
                      <p>Submitted projects appear here once available. Judges cannot evaluate their own project or team.</p>
                    )}
                    {!allowanceUsedUp && <Button variant="outline" onClick={handleRefresh}>Retry</Button>}
                  </div>
                )}
              </div>

              <div className="evaluations-manual-score-card" data-tour="eval-score">
                <h3>{isRubricCompetition ? "Evaluation" : "Manual Score"}</h3>

                <div className="evaluations-manual-picker" data-tour="eval-picker">
                  <div className="evaluations-manual-picker__header">
                    <h4>Available submissions</h4>
                    <button
                      type="button"
                      className="evaluations-manual-picker__refresh"
                      onClick={() => id && fetchManualSubmissionList(id)}
                      disabled={manualSubmissionListLoading || manualSelectionLoading}
                    >
                      Refresh
                    </button>
                  </div>

                  {usesProjectSubmissionLayout && (
                    <div className="evaluations-manual-picker__filters" data-tour="eval-tracks">
                      <label htmlFor="manual-track-filter">Track</label>
                      <select
                        id="manual-track-filter"
                        value={projectTrackFilter}
                        onChange={(e) => setProjectTrackFilter(e.target.value)}
                        disabled={manualSubmissionListLoading || manualSelectionLoading}
                      >
                        <option value="all">All tracks</option>
                        {projectTrackOptions.map((track) => (
                          <option key={`track-filter-${track}`} value={track}>
                            {getTrackLabel(track)}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {manualSubmissionListError && (
                    <p className="evaluations-error">{manualSubmissionListError}</p>
                  )}
                  {manualSubmissionListLoading ? (
                    <LoadingSpinner size="sm" text="Loading submissions..." />
                  ) : visibleManualSubmissionList.length === 0 ? (
                    <p className="evaluations-empty-text">
                      {allowanceUsedUp
                        ? ALLOWANCE_USED_UP_MESSAGE
                        : usesProjectSubmissionLayout && projectTrackFilter !== 'all'
                          ? 'No submissions for this track.'
                          : 'No submissions yet.'}
                    </p>
                  ) : (
                    <div className="evaluations-manual-picker__list">
                      {visibleManualSubmissionList.map((item) => {
                        const selected = manualSubmission?.submission_id === item.submission_id;
                        const title = getManualSubmissionDisplayLabel(item);
                        const submissionTrack = usesProjectSubmissionLayout ? getTrackLabel(item.metadata_track) : '';
                        const submissionTeam = (
                          (item.metadata_team || '').trim()
                          || item.team_name
                          || 'Individual submission'
                        );
                        return (
                          <button
                            type="button"
                            key={`manual-item-${item.submission_id}`}
                            className={`evaluations-manual-picker__item ${selected ? 'is-selected' : ''}`}
                            onClick={() => handleSelectManualSubmission(item.submission_id)}
                            disabled={manualSelectionLoading || submittingVote}
                          >
                            <div className="evaluations-manual-picker__item-title">{title}</div>
                            <div className="evaluations-manual-picker__item-team">
                              Team: {submissionTeam}
                            </div>
                            {usesProjectSubmissionLayout && (
                              <div className="evaluations-manual-picker__item-track">
                                Track: {submissionTrack}
                              </div>
                            )}
                            <div className="evaluations-manual-picker__item-meta">
                              {item.eval_count} evals
                              {item.already_scored ? ' • scored by you' : ''}
                              {' • '}
                              <RelativeTime value={item.submitted_at} />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {manualSelectionLoading && (
                  <div className="evaluations-hint evaluations-hint--manual">
                    Loading selected submission...
                  </div>
                )}

                {isRubricCompetition && rubric ? (
                  <RubricScoreForm key={`${rubric.id}-${manualSubmission?.submission_id || 'none'}-${manualSubmission?.submission_version || 0}`}
                    competitionId={Number(id)} rubric={rubric} submission={manualSubmission}
                    remaining={votesInfo ? Math.max(0, votesInfo.max_votes - votesInfo.current_votes) : 0}
                    feedbackRequired={feedbackRequired}
                    disabled={manualSelectionLoading} onBusyChange={setSubmittingVote}
                    onSubmitted={async () => { if (id) { await fetchVotesInfo(id); await fetchManualSubmissionList(id); } }}
                    onNext={handleRefresh} />
                ) : <>
                <label className="evaluations-manual-score-label" htmlFor="manual-score-input">
                  Score (1-100)
                </label>
                <input
                  id="manual-score-input"
                  type="number"
                  min={1}
                  max={100}
                  step={1}
                  value={manualScoreInput}
                  onChange={(e) => setManualScoreInput(e.target.value)}
                  className="evaluations-manual-score-input"
                  disabled={!manualSubmission || manualSelectionLoading || Boolean(manualScoreResult) || submittingVote}
                />
                <div className="evaluations-manual-score-hint">
                  {isCompetition18 && 'Enter one overall score based on the rubric. '}
                  Scores are averaged across all judges.
                </div>

                <div className="evaluations-feedback evaluations-feedback--manual">
                  <label className="evaluations-feedback-label" htmlFor="judge-feedback">
                    Feedback {feedbackRequired ? '(required)' : '(optional)'}
                  </label>
                  <textarea
                    id="judge-feedback"
                    className="evaluations-feedback-input"
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    placeholder="Provide feedback for this submission."
                    rows={4}
                    maxLength={1000}
                    disabled={!manualSubmission || manualSelectionLoading || Boolean(manualScoreResult) || submittingVote}
                  />
                  <div className="evaluations-feedback-hint">
                    {feedback.length}/1000{feedbackRequired ? ` (min ${minFeedbackChars})` : ''}
                  </div>
                </div>

                {feedbackRequired && !hasEnoughFeedback && !manualScoreResult && (
                  <div className="evaluations-hint evaluations-hint--manual">
                    Feedback is required before you can submit a score.
                  </div>
                )}

                {manualSubmission?.already_scored && !manualScoreResult && (
                  <div className="evaluations-hint evaluations-hint--manual">
                    You already scored this submission version. Select another submission or load a random one.
                  </div>
                )}

                {!manualScoreResult ? (
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleSubmitManualScore}
                    disabled={
                      submittingVote
                      || !manualSubmission
                      || manualSelectionLoading
                      || !manualScoreInput.trim()
                      || Boolean(manualSubmission?.already_scored)
                      || (feedbackRequired && !hasEnoughFeedback)
                    }
                    className="evaluations-vote-btn"
                  >
                    {submittingVote ? 'Submitting...' : 'Submit Score'}
                  </Button>
                ) : (
                  <div className="evaluations-vote-result evaluations-vote-result--manual">
                    <p className="evaluations-vote-message">
                      Submitted <strong>{manualScoreResult.score_submitted}</strong>
                    </p>
                    <p className="evaluations-manual-result-meta">
                      Current average: <strong>{manualScoreResult.average_score.toFixed(2)}</strong>
                      {' '}({manualScoreResult.total_scores} evaluations)
                    </p>
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={handleRefresh}
                      className="evaluations-refresh-btn"
                    >
                      Load Next Submission
                    </Button>
                  </div>
                )}
                </>}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="evaluations-images-container" data-tour="eval-pair">
              <SingleFileViewer
                type={competitionType}
                url={mediaPair[0].url}
                label={mediaPair[0]?.team_name ? `Submission A • ${mediaPair[0].team_name}` : 'Submission A'}
                onImageClick={setSelectedMediaUrl}
              />
              <SingleFileViewer
                type={competitionType}
                url={mediaPair[1].url}
                label={mediaPair[1]?.team_name ? `Submission B • ${mediaPair[1].team_name}` : 'Submission B'}
                onImageClick={setSelectedMediaUrl}
              />
            </div>

            {!vote && !isVideoCompetition && !isPdfCompetition && !isHtmlCompetition && !isGithubCompetition && (
              <div className="evaluations-hint evaluations-hint--below-media">
                <p>💡 Click on an image to view it in full size</p>
              </div>
            )}

            <div className="evaluations-feedback" data-tour="eval-feedback">
              <label className="evaluations-feedback-label" htmlFor="judge-feedback">
                Feedback {feedbackRequired ? '(required)' : '(optional)'}
              </label>
              <textarea
                id="judge-feedback"
                className="evaluations-feedback-input"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Provide feedback before voting below."
                rows={1}
                maxLength={1000}
                disabled={vote !== null || submittingVote}
              />
              <div className="evaluations-feedback-hint">
                {feedback.length}/1000{feedbackRequired ? ` (min ${minFeedbackChars})` : ''}
              </div>
            </div>

            {feedbackRequired && !hasEnoughFeedback && vote === null && (
              <div className="evaluations-hint evaluations-hint--spaced">
                Feedback is required before you can vote.
              </div>
            )}

            {voteDelayActive && (
              <div className="evaluations-vote-lock" role="status" aria-live="polite">
                <strong>Voting unlocks in {formatDelay(voteDelaySecondsLeft)}</strong>
                <span>For this competition, vote buttons become available 5 minutes after the pair loads.</span>
              </div>
            )}

            <div className="evaluations-actions" data-tour="eval-vote">
              <Button
                variant="primary"
                size="lg"
                onClick={() => handleVote('A')}
                disabled={vote !== null || submittingVote || voteDelayActive || (feedbackRequired && !hasEnoughFeedback)}
                className="evaluations-vote-btn"
              >
                Vote A
              </Button>

              <Button
                variant="secondary"
                size="lg"
                onClick={() => handleVote('Draw')}
                disabled={vote !== null || submittingVote || voteDelayActive || (feedbackRequired && !hasEnoughFeedback)}
                className="evaluations-draw-btn"
              >
                Vote Draw
              </Button>

              <Button
                variant="primary"
                size="lg"
                onClick={() => handleVote('B')}
                disabled={vote !== null || submittingVote || voteDelayActive || (feedbackRequired && !hasEnoughFeedback)}
                className="evaluations-vote-btn"
              >
                Vote B
              </Button>
            </div>

            {vote && !error && (
              <div className="evaluations-vote-result">
                <p className="evaluations-vote-message">You voted: <strong>{vote}</strong></p>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleRefresh}
                  className="evaluations-refresh-btn"
                >
                  Load Next Pair
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default Evaluations;
